import React, { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../../../config";
import "../../../styles/Bookings.css";

// An advertiser asking to swap the artwork on a booking this broadcaster has
// already approved.
//
// It is a separate decision from approving the booking, and it has to be,
// because approving a booking is approving a specific image for a specific
// window. Without this step, "book something innocuous, then replace it" is a
// way to put arbitrary content on a screen inside someone else's business —
// so the swap
// does not reach the screen until it is answered here.
//
// Both images are shown side by side. Approving on the strength of an id or a
// filename would not be review.

function when(value) {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function imageUrl(url) {
    if (!url) return null;
    return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

export default function CreativeChangeReview({ onReviewed }) {
    const [changes, setChanges] = useState([]);
    const [notes, setNotes] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const token = localStorage.getItem("token");

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/creative-changes`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setChanges(data.changes || []);
        } catch {
            setChanges([]);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    async function review(changeId, action) {
        setBusyId(changeId);
        setError(""); setNotice("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/vendor/creative-changes/${changeId}/${action}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ review_note: notes[changeId] || undefined }),
                }
            );
            const data = await res.json().catch(() => ({}));
            // A 409 here is real, and there are two kinds. The booking may have
            // been cancelled or the ad disabled by moderation since the request
            // was made -- or the advertiser has replaced this ad with a newer
            // one while the page sat open. The second is the common case and is
            // not a failure: reloading below puts the newer request on screen,
            // and the older image can never be approved onto the screen behind
            // it. Reported either way rather than swallowed.
            if (res.ok) setNotice(data.message || "Done.");
            else if (data.superseded) setNotice(data.message);
            else setError(data.message || "Could not review this change.");
        } catch {
            setError("Network error. Nothing changed.");
        }
        setBusyId(null);
        load();
        if (onReviewed) onReviewed();
    }

    if (changes.length === 0) return null;

    return (
        <section className="cr">
            <h2 className="cr__head">
                {changes.length === 1
                    ? "An advertiser wants to change their ad"
                    : `${changes.length} advertisers want to change their ad`}
            </h2>
            <p className="cr__lede">
                These bookings are already paid for and the dates are not changing.
                Until you approve, the screen keeps playing the ad you already
                approved.
            </p>

            {error && <div className="wb-note wb-note--danger">{error}</div>}
            {notice && !error && <div className="wb-note wb-note--ok">{notice}</div>}

            <div className="cr__list">
                {changes.map((change) => (
                    <article className="cr__item" key={change.id}>
                        <p className="cr__meta">
                            <strong>{change.device_name || `Screen #${change.device_id}`}</strong>
                            <br />
                            Booked {when(change.start_time)} – {when(change.end_time)} ·
                            requested {when(change.requested_at)}
                        </p>

                        <div className="cr__compare">
                            <div className="cr__side">
                                <span className="cr__label">Playing now</span>
                                {imageUrl(change.from_image_url) ? (
                                    <img
                                        className="cr__img"
                                        src={imageUrl(change.from_image_url)}
                                        alt="The ad currently approved for this screen"
                                    />
                                ) : (
                                    <div className="cr__img" />
                                )}
                            </div>
                            <span className="cr__arrow" aria-hidden="true">→</span>
                            <div className="cr__side">
                                <span className="cr__label">Proposed</span>
                                {imageUrl(change.to_image_url) ? (
                                    <img
                                        className="cr__img"
                                        src={imageUrl(change.to_image_url)}
                                        alt="The ad the advertiser wants to run instead"
                                    />
                                ) : (
                                    <div className="cr__img" />
                                )}
                            </div>
                        </div>

                        <div className="cr__actions">
                            <input
                                className="wb-input wb-input--sm"
                                placeholder="Note to the advertiser (optional)"
                                value={notes[change.id] || ""}
                                onChange={(e) =>
                                    setNotes({ ...notes, [change.id]: e.target.value })
                                }
                                aria-label="Note to the advertiser"
                            />
                            <button
                                type="button"
                                className="wb-btn wb-btn--primary"
                                onClick={() => review(change.id, "approve")}
                                disabled={busyId === change.id}
                            >
                                Approve new ad
                            </button>
                            <button
                                type="button"
                                className="wb-btn wb-btn--secondary"
                                onClick={() => review(change.id, "reject")}
                                disabled={busyId === change.id}
                            >
                                Decline
                            </button>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
